import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Users, User, UsersRound, CheckCircle2 } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { PageShell } from "@/components/PageShell";

const TYPES = [
  { id: "team", label: "Full Team", icon: UsersRound, desc: "4 players · €220", price: 220 },
  { id: "partial", label: "Partial Team", icon: Users, desc: "2–3 players · €55 each", price: 55 },
  { id: "individual", label: "Individual", icon: User, desc: "Just me · €55 — we'll pair you", price: 55 },
];

export default function Register() {
  const nav = useNavigate();
  const [entryType, setEntryType] = useState("team");
  const [form, setForm] = useState({ team_name: "", captain_name: "", email: "", phone: "", notes: "" });
  const [players, setPlayers] = useState([{ name: "", handicap: "" }]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setPlayer = (i, k, v) => setPlayers((p) => p.map((pl, idx) => (idx === i ? { ...pl, [k]: v } : pl)));

  const amount = entryType === "team" ? 220 : 55 * Math.max(1, players.filter((p) => p.name).length || 1);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.captain_name || !form.email || !form.phone) { toast.error("Please fill in your name, email and phone."); return; }
    setSubmitting(true);
    try {
      const payload = {
        entry_type: entryType,
        team_name: form.team_name,
        captain_name: form.captain_name,
        email: form.email,
        phone: form.phone,
        notes: form.notes,
        players: players.filter((p) => p.name).map((p) => ({ name: p.name, handicap: p.handicap })),
      };
      const { data } = await api.post("/registrations", payload);
      await api.post("/payments/checkout", { registration_id: data.id, amount: data.amount });
      setDone({ ...data, payment_status: "paid" });
      toast.success("Entry confirmed — payment received!");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(apiErr(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <PageShell>
        <div className="max-w-2xl mx-auto px-5 py-24 text-center">
          <div className="mx-auto h-20 w-20 rounded-full bg-green-100 grid place-items-center">
            <CheckCircle2 className="text-green-600" size={44} />
          </div>
          <h1 className="mt-6 font-display text-4xl text-slate-900">You're in!</h1>
          <p className="mt-3 text-slate-600 text-lg">
            Thank you, {done.captain_name}. Your {done.entry_type} entry is confirmed and payment of
            <span className="font-semibold text-slate-900"> €{done.amount}</span> has been received.
          </p>
          <div className="mt-6 glass rounded-2xl p-6 text-left border border-slate-100" data-testid="reg-confirmation">
            <p className="text-sm text-slate-500">Confirmation reference</p>
            <p className="font-mono text-sm text-slate-900">{done.id}</p>
            <p className="mt-3 text-sm text-slate-600">A confirmation email has been sent to {done.email}. You'll receive WhatsApp updates before and during the event.</p>
          </div>
          <div className="mt-8 flex justify-center gap-3">
            <button onClick={() => nav("/leaderboard")} className="px-6 py-3 rounded-full bg-sky-600 text-white font-semibold">View Leaderboard</button>
            <button onClick={() => setDone(null)} className="px-6 py-3 rounded-full bg-slate-100 font-semibold">Add another entry</button>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <div className="max-w-3xl mx-auto px-5 lg:px-8 py-16">
        <p className="text-sky-600 text-sm uppercase tracking-[0.25em] font-semibold">Registration</p>
        <h1 className="mt-3 font-display text-4xl lg:text-5xl text-slate-900">Enter the Classic</h1>
        <p className="mt-3 text-slate-600 text-lg">Enter as a team, partial team, or as an individual. Four person team event at Elmgreen Golf Club.</p>

        <form onSubmit={submit} className="mt-10 space-y-8">
          <div className="grid sm:grid-cols-3 gap-3">
            {TYPES.map((t) => (
              <button key={t.id} type="button" data-testid={`entry-type-${t.id}`} onClick={() => setEntryType(t.id)}
                className={`text-left rounded-2xl border-2 p-5 transition-all ${entryType === t.id ? "border-sky-600 bg-sky-50 -translate-y-0.5 shadow-md" : "border-slate-200 hover:border-sky-300"}`}>
                <t.icon className={entryType === t.id ? "text-sky-600" : "text-slate-400"} />
                <p className="mt-3 font-semibold text-slate-900">{t.label}</p>
                <p className="text-sm text-slate-500">{t.desc}</p>
              </button>
            ))}
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {entryType !== "individual" && (
              <Field label="Team name" value={form.team_name} onChange={(v) => set("team_name", v)} testid="reg-team-name" placeholder="The Eagles" />
            )}
            <Field label="Your name (captain)" required value={form.captain_name} onChange={(v) => set("captain_name", v)} testid="reg-captain-name" placeholder="Liam Ivory" />
            <Field label="Email" required type="email" value={form.email} onChange={(v) => set("email", v)} testid="reg-email" placeholder="you@email.com" />
            <Field label="Phone" required value={form.phone} onChange={(v) => set("phone", v)} testid="reg-phone" placeholder="086 000 0000" />
          </div>

          {entryType !== "individual" && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900">Players</h3>
                <button type="button" data-testid="add-player-btn" onClick={() => setPlayers((p) => [...p, { name: "", handicap: "" }])}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-sky-700 hover:gap-2 transition-all">
                  <Plus size={16} /> Add player
                </button>
              </div>
              <div className="space-y-3">
                {players.map((p, i) => (
                  <div key={i} className="flex gap-3 items-center">
                    <input data-testid={`player-name-${i}`} className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 focus:ring-2 focus:ring-sky-500 outline-none" placeholder={`Player ${i + 1} name`} value={p.name} onChange={(e) => setPlayer(i, "name", e.target.value)} />
                    <input data-testid={`player-handicap-${i}`} className="w-28 rounded-xl border border-slate-200 px-4 py-2.5 focus:ring-2 focus:ring-sky-500 outline-none" placeholder="Hcp" value={p.handicap} onChange={(e) => setPlayer(i, "handicap", e.target.value)} />
                    {players.length > 1 && (
                      <button type="button" onClick={() => setPlayers((pl) => pl.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-500"><Trash2 size={18} /></button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-slate-700">Notes (optional)</label>
            <textarea data-testid="reg-notes" className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-2.5 focus:ring-2 focus:ring-sky-500 outline-none" rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Dietary requirements, buggy needs, etc." />
          </div>

          <div className="glass rounded-2xl p-5 flex items-center justify-between border border-slate-100">
            <div>
              <p className="text-sm text-slate-500">Total due</p>
              <p className="font-display text-3xl text-slate-900" data-testid="reg-total">€{amount}</p>
            </div>
            <button type="submit" disabled={submitting} data-testid="reg-submit-btn" className="px-8 py-3.5 rounded-full bg-green-600 text-white font-semibold shadow-lg hover:-translate-y-0.5 transition-all disabled:opacity-60">
              {submitting ? "Processing…" : "Pay & Confirm"}
            </button>
          </div>
          <p className="text-xs text-slate-400 text-center">Secure payment powered by Stripe. This demo uses a test payment flow.</p>
        </form>
      </div>
    </PageShell>
  );
}

function Field({ label, value, onChange, type = "text", required, placeholder, testid }) {
  return (
    <div>
      <label className="text-sm font-medium text-slate-700">{label}{required && <span className="text-red-500"> *</span>}</label>
      <input data-testid={testid} type={type} required={required} value={value} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-2.5 focus:ring-2 focus:ring-sky-500 outline-none transition-shadow" />
    </div>
  );
}
